/**
 * IntegrationSaveFailure — the classified reason a saveIntegration call
 * failed in transport or authorization rather than verification, resolved
 * from whatever the client caught: the server's own `details.reason` when
 * present, else the coarser `HttpsError`/`FunctionsError` code, else
 * `unknown`. The panel's reconciler
 * (web/src/admin/settings/integrations/integrations.sync.ts) calls
 * `resolveFailureReason` instead of collapsing every failure to one
 * hardcoded catalog string, so the panel shows cause-specific copy. A
 * verification outcome is never one of these — it travels as the callable's
 * own return value under IntegrationVerificationReasonSchema below.
 *
 * Reasons justified — one member per `throw new HttpsError(...)` site in
 * the saveIntegration handler
 * (firebase/src/settings/integrations.api.ts):
 *   unauthenticated — no auth on the request
 *   notAuthorized   — the mfa claim or the credentials-manage permission
 *                     is missing from the caller's grant → role chain
 *   invalidInput    — the payload failed the registry-derived union parse
 * `unknown` is the mandatory fallback: an error `resolveFailureReason`
 * cannot classify, or a value that is not an error at all.
 */
import { z } from "zod";

export const ReasonSchema = z.enum([
	"unauthenticated",
	"notAuthorized",
	"invalidInput",
	"unknown",
]);
export type Reason = z.infer<typeof ReasonSchema>;

/**
 * IntegrationVerificationReason — the closed outcome vocabulary of a generic
 * integration save's verification: the saveIntegration callable proves a
 * credential by using it (integrations.const.ts's `Verify<Id>`) and
 * classifies the outcome into exactly these five members, stored on
 * `WorkspaceSettings.integrations[id].lastVerifiedReason`
 * (workspace.entity.ts). Provider specificity never grows a member here —
 * the provider's own message travels beside the reason as free text
 * (`lastVerifiedDetail`), so one vocabulary serves every registry id.
 *
 * What earns a member is a distinct REMEDY. Every consumer maps the member to
 * the thing it tells an operator to go and do, so two conditions sharing one
 * member send one of them somewhere that cannot fix it.
 *
 * Members justified:
 *   ok            — the verify call succeeded; the credential proved itself
 *                   by use.
 *   invalid       — the provider rejected the credential itself: malformed,
 *                   expired, or not one it recognises. Mint a new one.
 *   unreachable   — the provider could not be reached or the call never
 *                   completed — says nothing about the credential either way.
 *                   Try again.
 *   notAuthorized — the provider answered but refused: the credential is
 *                   real yet lacks the grant the verify exercises. Assign the
 *                   asset or widen the grant.
 *   notConfigured — a precondition the integration cannot operate without is
 *                   absent, and the credential is not at fault: the save is
 *                   refused over a SETTING, which `lastVerifiedDetail` names.
 *                   Neither transient nor about credentials, so none of the
 *                   three above carries it without sending the operator to a
 *                   remedy that cannot work. The word is the one the ads
 *                   client, the wallet deliveries and the ad-conversion
 *                   delivery already use for this same condition, so one
 *                   mechanism answers to one name wherever it is reported.
 */
export const IntegrationVerificationReasonSchema = z.enum([
	"ok",
	"invalid",
	"unreachable",
	"notAuthorized",
	"notConfigured",
]);
export type IntegrationVerificationReason = z.infer<
	typeof IntegrationVerificationReasonSchema
>;

// The second-tier fallback: an `HttpsError` code, for an older deployed
// function that has not yet been updated to attach `details.reason`.
// Deliberately partial — a code that names no single distinct cause
// (`internal`: any uncaught crash) is left OUT of this map rather than
// guessed, because a specific-but-WRONG message misleads the operator worse
// than the honest generic text `unknown` degrades to.
//
// Keyed on the bare core code string (`"invalid-argument"`), never the
// client SDK's `"functions/invalid-argument"` — `resolveFailureReason`
// strips that prefix before this lookup, so the same map serves a raw
// server-side `HttpsError.code` too.
const CODE_TO_REASON: Partial<Record<string, Reason>> = {
	unauthenticated: "unauthenticated",
	"permission-denied": "notAuthorized",
	"invalid-argument": "invalidInput",
};

function readDetailsReason(details: unknown): Reason | null {
	if (typeof details !== "object" || details === null) return null;
	if (!("reason" in details)) return null;
	const parsed = ReasonSchema.safeParse(
		(details as { reason: unknown }).reason,
	);
	return parsed.success ? parsed.data : null;
}

function readCode(err: unknown): string | null {
	if (typeof err !== "object" || err === null) return null;
	if (!("code" in err)) return null;
	const code = (err as { code: unknown }).code;
	return typeof code === "string" ? code.replace(/^functions\//, "") : null;
}

/**
 * resolveFailureReason — the one classification ladder the panel's `.sync`
 * reconciler calls rather than duplicating: `details.reason` (when the
 * server set one and it names a real `Reason`) → the bare `HttpsError`/
 * `FunctionsError` code → `unknown`. Pure and never throws — a garbage,
 * null, or undefined input resolves `unknown` like any other error this
 * function cannot classify.
 */
export function resolveFailureReason(err: unknown): Reason {
	if (typeof err === "object" && err !== null && "details" in err) {
		const fromDetails = readDetailsReason(
			(err as { details: unknown }).details,
		);
		if (fromDetails) return fromDetails;
	}
	const code = readCode(err);
	if (code) {
		const mapped = CODE_TO_REASON[code];
		if (mapped) return mapped;
	}
	return "unknown";
}
